'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const researchItems = [
  {
    title: 'PALISADES PICNIC',
    category: 'Construction Systems',
    year: 'Ongoing',
    excerpt:
      'Exploration of cast-in-place concrete surfaces, pigmentation, grinding methods, and long-term ageing characteristics.',
    image: `${process.env.PUBLIC_URL}/images/research/palisadespicnic/thumb.jpg`,
    route: '/projects/soon-project',
  },
  {
    title: 'COLLECTIVE ASSEMBLIES',
    category: 'Spatial Studies',
    year: '2023',
    excerpt:
      'Analyzing historic and contemporary courtyard houses to quantify passive cooling performance and spatial efficiency.',
    image: `${process.env.PUBLIC_URL}/images/research/collectiveassemblies/thumb.jpg`,
    route: '/projects/soon-project',
  }, 
  {
    title: 'RESET STUDIO',
    category: 'Materials Research',
    year: '2024',
    excerpt:
      'A comparative study of natural and industrial materials under high humidity, salinity, and temperature fluctuation in coastal India.',
    image: `${process.env.PUBLIC_URL}/images/research/resetstudio/thumb.jpg`,
    route: '/projects/soon-project',
  }, 
  {
    title: 'TRANSRESOLUTIONARY LEGIBILITY OF ARCHITECTURE',
    category: 'Construction Systems',
    year: 'Ongoing',
    excerpt:
      'Exploration of cast-in-place concrete surfaces, pigmentation, grinding methods, and long-term ageing characteristics.',
    image: `${process.env.PUBLIC_URL}/images/research/thesis/thumb.jpg`,
    route: '/projects/soon-project',
  }, 
];

const ResearchPage = () => {
  const navigate = useNavigate();
  const heroImage = `${process.env.PUBLIC_URL}/images/Hero/researchhero.webp`;

  return (
    <div className="bg-white text-black min-h-screen font-sans">
      <Navbar />

      {/* Hero Section */}
      <section
        id="hero"
        className="relative h-[60vh] flex flex-col justify-center items-center overflow-hidden bg-black"
      >
        <img
          src={heroImage}
          alt="Research Hero"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
      
        <motion.div
          className="relative z-10 text-center px-6 text-white"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <h1 className="max-w-[92vw] text-center text-[clamp(2.5rem,10vw,7rem)] leading-[0.95] break-words font-heading font-light mb-4 tracking-wide">
            RESEARCH
          </h1>
        </motion.div>
      </section>


      {/* Research Grid */}
      <section className="py-24 px-8 md:px-20 bg-gray-50">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-20">
          {researchItems.map((item, index) => (
            <motion.article
              key={index}
              onClick={() => navigate(item.route)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(item.route)}
              className="group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
            >
              {/* Image Plate */}
              <div className="relative aspect-[16/9] overflow-hidden rounded-none group-hover:rounded-2xl transition-all duration-500 ease-out">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700 ease-out"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500" />

                {/* Excerpt only on hover */}
                <div className="absolute inset-0 flex items-end p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="text-white/80 text-xs leading-relaxed max-w-[90%]">
                    {item.excerpt}
                  </p>
                </div>
              </div>

              {/* Editorial Caption */}
              <div className="mt-4 space-y-1">
                <div className="flex justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>{item.category}</span>
                  <span>{item.year}</span>
                </div>
                <h3 className="text-base font-medium tracking-wide text-gray-900 leading-snug">
                  {item.title}
                </h3>
                <div className="h-[1px] w-12 bg-gray-300 mt-2 group-hover:w-24 transition-all duration-500" />
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Closing note */}
      <section className="pb-24 px-6 max-w-3xl mx-auto text-center">
        <p className="text-sm text-gray-500 leading-relaxed">
          Our research is ongoing and iterative. Selected studies are published
          here as working documents that continue to evolve through built
          projects and material experimentation.
        </p>
      </section>

      <Footer />
    </div>
  );
};

export default ResearchPage;
